import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/common/Button'
import type { CommercialSignal } from '@/hooks/useCommercialSignals'

interface Props {
  signals: CommercialSignal[]
  title?: string
  limit?: number
  loading?: boolean
  compact?: boolean
  onRefresh?: () => Promise<unknown>
  onTransition?: (id: string, body: Record<string, unknown>) => Promise<unknown>
}

function actionHref(signal: CommercialSignal): string {
  if (signal.recommendedAction.code === 'review_evidence') return `/relaciones/${signal.organizationId}#evidencia-m365`
  if (signal.personId && signal.recommendedAction.code.includes('contact')) return `/personas/${signal.personId}`
  return `/relaciones/${signal.organizationId}#nueva-accion`
}

export const CommercialSignalsPanel: React.FC<Props> = ({ signals, title = 'Señales detectadas', limit = 3, loading = false, compact = false, onRefresh, onTransition }) => {
  const [busy, setBusy] = React.useState<string | null>(null)
  const [message, setMessage] = React.useState<string | null>(null)
  const visible = signals.slice(0, limit)

  const run = async (key: string, task: () => Promise<unknown>, success: string) => {
    setBusy(key); setMessage(null)
    try { await task(); setMessage(success) }
    catch { setMessage('No se pudo verificar el cambio. Reintenta sin duplicar la acción.') }
    finally { setBusy(null) }
  }

  return (
    <section className={`commercialSignals${compact ? ' commercialSignals--compact' : ''}`} aria-labelledby="commercial-signals-title">
      <div className="commercialSignals__header">
        <div>
          <span className="commercialSignals__eyebrow">Radar comercial · signals-v1</span>
          <h2 id="commercial-signals-title">{title}</h2>
          <p>Situaciones para revisar. Una señal es una inferencia; la historia confirmada permanece separada.</p>
        </div>
        {onRefresh && <Button size="sm" variant="secondary" disabled={loading || Boolean(busy)} onClick={() => run('refresh', onRefresh, 'Radar actualizado con resultado verificable.')}>{loading || busy === 'refresh' ? 'Actualizando…' : 'Actualizar señales'}</Button>}
      </div>
      {visible.length === 0 ? (
        <div className="commercialSignals__empty">No hay señales activas materializadas para revisar.</div>
      ) : (
        <div className="commercialSignals__list">
          {visible.map((signal) => (
            <article key={signal.id} className={`commercialSignalCard commercialSignalCard--${signal.kind} commercialSignalCard--${signal.severity}`}>
              <div className="commercialSignalCard__topline">
                <span className={`commercialSignalCard__kind commercialSignalCard__kind--${signal.kind}`}>
                  {signal.state === 'indeterminate'
                    ? 'Estado incierto · fuente incompleta'
                    : signal.kind === 'discovery' ? 'Posible · pendiente de revisión' : 'Inferencia sobre hechos confirmados'}
                </span>
                <span>{signal.organizationName}</span>
              </div>
              <h3>{signal.title}</h3>
              <p><strong>Qué detectó Sales:</strong> {signal.explanation}</p>
              <p className="commercialSignalCard__recommendation"><strong>Siguiente acción:</strong> {signal.recommendedAction.label}</p>
              <div className="commercialSignalCard__actions">
                <Link className="commercialPrimaryLink" to={actionHref(signal)}>Actuar</Link>
                <Link className="prototypeLink" to={`/relaciones/${signal.organizationId}#evidencia-m365`}>Revisar evidencia</Link>
                {onTransition && <button type="button" disabled={Boolean(busy)} onClick={() => run(`snooze-${signal.id}`, () => onTransition(signal.id, { action: 'snooze', until: new Date(Date.now() + 7 * 86_400_000).toISOString() }), 'Señal pospuesta 7 días.')}>Posponer</button>}
                {onTransition && <button type="button" disabled={Boolean(busy)} onClick={() => run(`dismiss-${signal.id}`, () => onTransition(signal.id, { action: 'dismiss', reason: 'Descartada explícitamente por el usuario desde la interfaz.' }), 'Señal descartada.')}>Descartar</button>}
              </div>
            </article>
          ))}
        </div>
      )}
      {message && <p className="commercialSignals__feedback" role="status">{message}</p>}
    </section>
  )
}
